import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { RECAPTCHA_SITE_KEY } from '../../../config/env';

/**
 * ReCaptcha component that renders Google reCAPTCHA v2
 * @param {function} onVerify - Callback when captcha is verified, receives token
 * @param {function} onExpire - Callback when captcha expires
 * @param {function} onError - Callback when captcha encounters an error
 * @param {ref} ref - Ref to expose reset method
 */
const ReCaptcha = forwardRef(({ onVerify, onExpire, onError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const isRenderedRef = useRef(false);
  const [isDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Store callbacks in refs to avoid re-rendering
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onVerify, onExpire, onError]);

  // Expose reset method to parent (must be before any early returns)
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch (err) {
          console.error('Error resetting reCAPTCHA:', err);
        }
      }
    }
  }));

  useEffect(() => {
    // Skip if no site key configured
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('RECAPTCHA_SITE_KEY not configured');
      return;
    }

    // Skip if already rendered (prevents double render in React Strict Mode)
    if (isRenderedRef.current) {
      return;
    }

    // Wait for grecaptcha to be available
    const renderCaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render && containerRef.current) {
        try {
          // Check if container already has a widget
          if (containerRef.current.hasChildNodes()) {
            console.warn('reCAPTCHA container already has children, skipping render');
            return;
          }

          // Only render if not already rendered
          if (widgetIdRef.current === null) {
            widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
              sitekey: RECAPTCHA_SITE_KEY,
              theme: isDark ? 'dark' : 'light',
              callback: (token) => {
                if (onVerifyRef.current) onVerifyRef.current(token);
              },
              'expired-callback': () => {
                if (onExpireRef.current) onExpireRef.current();
              },
              'error-callback': (error) => {
                console.error('reCAPTCHA error:', error);
                if (onErrorRef.current) onErrorRef.current(error);
              },
            });
            isRenderedRef.current = true;
          }
        } catch (err) {
          console.error('reCAPTCHA render error:', err);
          if (onError) onError(err);
        }
      }
    };

    // Check if grecaptcha is already loaded
    if (window.grecaptcha && window.grecaptcha.render) {
      renderCaptcha();
    } else {
      // Wait for the script to load
      const checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(checkInterval);
          renderCaptcha();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        console.error('reCAPTCHA script failed to load after 10 seconds');
      }, 10000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }

    // Cleanup on unmount
    return () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
          isRenderedRef.current = false;
        } catch (err) {
          // Ignore cleanup errors
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once

  // Don't render anything if no site key
  if (!RECAPTCHA_SITE_KEY) {
    return null;
  }

  return <div ref={containerRef} className="recaptcha-container" />;
});

export default ReCaptcha;
